import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/useToast';
import { useUser } from '@/hooks/useUser';
import { 
  QCTemplateWithParameters, 
  QCParameter, 
  QCParameterFormData,
  QCTemplateFormData,
  QCTemplateFilter
} from '@/types/qc';

export const useQCTemplates = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<QCTemplateWithParameters[]>([]);
  const [template, setTemplate] = useState<QCTemplateWithParameters | null>(null);
  const { toast } = useToast();
  const { user } = useUser();

  // Fetch all templates with optional filtering
  const fetchTemplates = useCallback(async (filters: QCTemplateFilter = {}) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('qc_templates_with_parameters')
        .select('*');
      
      // Apply filters
      if (filters.search) {
        query = query.or(`template_name.ilike.%${filters.search}%,template_code.ilike.%${filters.search}%`);
      }
      
      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      
      // Execute query
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching QC templates:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch QC templates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch a single template by ID with its parameters
  const fetchTemplateById = useCallback(async (id: string) => {
    try {
      setLoading(true);
      
      // Get the template details
      const { data, error } = await supabase.rpc('get_qc_template_with_parameters', {
        template_id_param: id
      });
      
      if (error) {
        throw error;
      }
      
      if (!data || !data.template) {
        throw new Error('Template not found');
      }
      
      const templateData: QCTemplateWithParameters = {
        ...data.template,
        parameters: data.parameters || [],
        parameter_count: data.parameters?.length || 0,
        created_by_name: data.created_by?.full_name
      };
      
      setTemplate(templateData);
      return templateData;
    } catch (error) {
      console.error('Error fetching QC template details:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch QC template details',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Check if a template code already exists
  const checkTemplateCodeExists = useCallback(async (code: string, exceptId?: string) => {
    try {
      let query = supabase
        .from('qc_templates')
        .select('id')
        .eq('template_code', code);
      
      if (exceptId) {
        query = query.neq('id', exceptId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking template code:', error);
      return false;
    }
  }, []);

  // Generate a template code based on type
  const generateTemplateCode = useCallback(async (type: string) => {
    try {
      const { data, error } = await supabase.rpc('generate_qc_template_code', {
        template_type: type
      });
      
      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error generating template code:', error);
      return null;
    }
  }, []);

  // Create a new template with parameters
  const createTemplate = useCallback(async (
    templateData: QCTemplateFormData,
    parameters: QCParameterFormData[]
  ) => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'You must be logged in to create a template',
        variant: 'destructive',
      });
      return null;
    }
    
    try {
      setLoading(true);
      
      // Generate template code if not provided
      let template_code = templateData.template_code;
      if (!template_code) {
        template_code = await generateTemplateCode(templateData.type);
        if (!template_code) {
          throw new Error('Failed to generate template code');
        }
      }
      
      // Insert template first
      const { data: templateResult, error: templateError } = await supabase
        .from('qc_templates')
        .insert({
          template_code,
          template_name: templateData.template_name,
          type: templateData.type,
          description: templateData.description || null,
          status: templateData.status,
          created_by: user.id
        })
        .select()
        .single();
      
      if (templateError) {
        throw templateError;
      }
      
      // Check if we have parameters to insert
      if (parameters && parameters.length > 0) {
        // Prepare parameters data with template_id
        const parametersToInsert = parameters.map((param, index) => ({
          template_id: templateResult.id,
          parameter_name: param.parameter_name,
          uom_id: param.uom_id || null, // The database trigger will handle UOM code to ID conversion
          min_spec: param.min_spec !== null ? param.min_spec : null,
          max_spec: param.max_spec !== null ? param.max_spec : null,
          tolerance: param.tolerance || null,
          method: param.method,
          is_required: param.is_required,
          sequence_no: param.sequence_no || (index + 1) * 10
        }));
        
        // Insert parameters
        const { error: parametersError } = await supabase
          .from('qc_parameters')
          .insert(parametersToInsert);
        
        if (parametersError) {
          throw parametersError;
        }
      }
      
      toast({
        title: 'Success',
        description: 'QC template created successfully',
      });
      
      // Refresh templates list
      await fetchTemplates();
      
      return templateResult;
    } catch (error) {
      console.error('Error creating QC template:', error);
      toast({
        title: 'Error',
        description: 'Failed to create QC template',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [user, toast, generateTemplateCode, fetchTemplates]);

  // Update an existing template with parameters
  const updateTemplate = useCallback(async (
    id: string,
    templateData: QCTemplateFormData,
    parameters: QCParameterFormData[]
  ) => {
    try {
      setLoading(true);
      
      // Update template
      const { error: templateError } = await supabase
        .from('qc_templates')
        .update({
          template_name: templateData.template_name,
          type: templateData.type,
          description: templateData.description || null,
          status: templateData.status
        })
        .eq('id', id);
      
      if (templateError) {
        throw templateError;
      }
      
      // Get existing parameters
      const { data: existingParams, error: paramsError } = await supabase
        .from('qc_parameters')
        .select('id')
        .eq('template_id', id);
      
      if (paramsError) {
        throw paramsError;
      }
      
      const existingParamIds = (existingParams || []).map(p => p.id);
      const newParamIds = parameters.filter(p => p.id).map(p => p.id as string);
      
      // Parameters to delete (existing but not in new list)
      const paramsToDelete = existingParamIds.filter(id => !newParamIds.includes(id));
      
      // Delete removed parameters
      if (paramsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('qc_parameters')
          .delete()
          .in('id', paramsToDelete);
        
        if (deleteError) {
          throw deleteError;
        }
      }
      
      // Update or insert parameters
      for (const param of parameters) {
        if (param.id) {
          // Update existing parameter
          const { error: updateError } = await supabase
            .from('qc_parameters')
            .update({
              parameter_name: param.parameter_name,
              uom_id: param.uom_id || null, // The database trigger will handle UOM code to ID conversion
              min_spec: param.min_spec !== null ? param.min_spec : null,
              max_spec: param.max_spec !== null ? param.max_spec : null,
              tolerance: param.tolerance || null,
              method: param.method,
              is_required: param.is_required,
              sequence_no: param.sequence_no || 10
            })
            .eq('id', param.id);
          
          if (updateError) {
            throw updateError;
          }
        } else {
          // Insert new parameter
          const { error: insertError } = await supabase
            .from('qc_parameters')
            .insert({
              template_id: id,
              parameter_name: param.parameter_name,
              uom_id: param.uom_id || null, // The database trigger will handle UOM code to ID conversion
              min_spec: param.min_spec !== null ? param.min_spec : null,
              max_spec: param.max_spec !== null ? param.max_spec : null,
              tolerance: param.tolerance || null,
              method: param.method,
              is_required: param.is_required,
              sequence_no: param.sequence_no || 10
            });
          
          if (insertError) {
            throw insertError;
          }
        }
      }
      
      toast({
        title: 'Success',
        description: 'QC template updated successfully',
      });
      
      // Refresh template details
      await fetchTemplateById(id);
      
      return true;
    } catch (error) {
      console.error('Error updating QC template:', error);
      toast({
        title: 'Error',
        description: 'Failed to update QC template',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchTemplateById]);

  // Delete a template (this will cascade delete parameters)
  const deleteTemplate = useCallback(async (id: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('qc_templates')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'QC template deleted successfully',
      });
      
      // Refresh templates list
      await fetchTemplates();
      
      return true;
    } catch (error) {
      console.error('Error deleting QC template:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete QC template',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchTemplates]);

  // Change template status (activate/deactivate)
  const changeTemplateStatus = useCallback(async (id: string, status: 'Active' | 'Inactive') => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('qc_templates')
        .update({ status })
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: `QC template ${status === 'Active' ? 'activated' : 'deactivated'} successfully`,
      });
      
      // Refresh templates list
      await fetchTemplates();
      
      return true;
    } catch (error) {
      console.error('Error changing QC template status:', error);
      toast({
        title: 'Error',
        description: 'Failed to change QC template status',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchTemplates]);

  // Assign a QC template to a routing operation
  const assignTemplateToOperation = useCallback(async (
    routing_operation_id: string,
    qc_template_id: string
  ) => {
    try {
      setLoading(true);
      
      // Check if assignment already exists
      const { data: existingAssignment, error: checkError } = await supabase
        .from('routing_operation_qc_templates')
        .select('id')
        .eq('routing_operation_id', routing_operation_id)
        .eq('qc_template_id', qc_template_id);
      
      if (checkError) {
        throw checkError;
      }
      
      if (existingAssignment && existingAssignment.length > 0) {
        // Assignment already exists
        toast({
          title: 'Info',
          description: 'This template is already assigned to the operation',
        });
        return true;
      }
      
      // Create new assignment
      const { error } = await supabase
        .from('routing_operation_qc_templates')
        .insert({
          routing_operation_id,
          qc_template_id
        });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'QC template assigned to operation successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error assigning QC template to operation:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign QC template to operation',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Remove a QC template assignment from a routing operation
  const removeTemplateFromOperation = useCallback(async (
    routing_operation_id: string,
    qc_template_id: string
  ) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('routing_operation_qc_templates')
        .delete()
        .eq('routing_operation_id', routing_operation_id)
        .eq('qc_template_id', qc_template_id);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'QC template removed from operation successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error removing QC template from operation:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove QC template from operation',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Assign a QC template to a BOM component
  const assignTemplateToComponent = useCallback(async (
    bom_component_id: string,
    qc_template_id: string
  ) => {
    try {
      setLoading(true);
      
      // Check if assignment already exists
      const { data: existingAssignment, error: checkError } = await supabase
        .from('component_qc_templates')
        .select('id')
        .eq('bom_component_id', bom_component_id)
        .eq('qc_template_id', qc_template_id);
      
      if (checkError) {
        throw checkError;
      }
      
      if (existingAssignment && existingAssignment.length > 0) {
        // Assignment already exists
        toast({
          title: 'Info',
          description: 'This template is already assigned to the component',
        });
        return true;
      }
      
      // Create new assignment
      const { error } = await supabase
        .from('component_qc_templates')
        .insert({
          bom_component_id,
          qc_template_id
        });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'QC template assigned to component successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error assigning QC template to component:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign QC template to component',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Remove a QC template assignment from a BOM component
  const removeTemplateFromComponent = useCallback(async (
    bom_component_id: string,
    qc_template_id: string
  ) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('component_qc_templates')
        .delete()
        .eq('bom_component_id', bom_component_id)
        .eq('qc_template_id', qc_template_id);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'QC template removed from component successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error removing QC template from component:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove QC template from component',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch routing operations with their QC templates
  const fetchRoutingOperationsWithQC = useCallback(async (template_id: string) => {
    try {
      setLoading(true);
      
      // First get all routing operation QC templates with given template ID
      const { data, error } = await supabase
        .from('routing_operation_qc_templates')
        .select('id, routing_operation_id, qc_template_id')
        .eq('qc_template_id', template_id);
      
      if (error) {
        throw error;
      }

      // If we have data, get the related routing operations
      if (data && data.length > 0) {
        const routingOperationIds = data.map(item => item.routing_operation_id);
        
        const { data: routingOpsData, error: routingOpsError } = await supabase
          .from('routing_operations')
          .select(`
            id, 
            routing_id,
            sequence_no,
            operation_id
          `)
          .in('id', routingOperationIds);
          
        if (routingOpsError) {
          throw routingOpsError;
        }
        
        // Join the data manually
        const mergedData = data.map(qcItem => {
          const routingOp = routingOpsData?.find(op => op.id === qcItem.routing_operation_id);
          return {
            ...qcItem,
            routing_id: routingOp?.routing_id,
            sequence_no: routingOp?.sequence_no,
            operation_id: routingOp?.operation_id
          };
        });
        
        return mergedData || [];
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching routing operations with QC:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch routing operations with QC details',
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch BOM components with their QC templates
  const fetchBOMComponentsWithQC = useCallback(async (template_id: string) => {
    try {
      setLoading(true);
      
      // Using the correct table name 'bom_components_qc_templates' with fields that exist
      const { data, error } = await supabase
        .from('bom_components_qc_templates')
        .select('*')
        .eq('qc_template_id', template_id);
      
      if (error) {
        throw error;
      }
      
      // We already have all the fields we need in a single view
      return data || [];
    } catch (error) {
      console.error('Error fetching BOM components with QC:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch BOM components with QC details',
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Clone a QC template
  const cloneTemplate = useCallback(async (templateId: string, newName: string) => {
    try {
      setLoading(true);
      
      // Fetch the template to clone
      const templateToClone = await fetchTemplateById(templateId);
      if (!templateToClone) {
        throw new Error('Template not found');
      }
      
      // Generate a new code for the clone
      const cloneCode = await generateTemplateCode(templateToClone.type);
      if (!cloneCode) {
        throw new Error('Failed to generate template code');
      }
      
      // Create a new template based on the original
      const templateFormData: QCTemplateFormData = {
        template_code: cloneCode,
        template_name: newName,
        type: templateToClone.type,
        description: templateToClone.description,
        status: 'Active', // Always set cloned templates to active
      };
      
      // Clone parameters
      const parametersFormData: QCParameterFormData[] = templateToClone.parameters.map(param => ({
        parameter_name: param.parameter_name,
        uom_id: param.uom_id,
        min_spec: param.min_spec,
        max_spec: param.max_spec,
        tolerance: param.tolerance,
        method: param.method,
        is_required: param.is_required,
        sequence_no: param.sequence_no
      }));
      
      // Create the cloned template
      const result = await createTemplate(templateFormData, parametersFormData);
      
      if (!result) {
        throw new Error('Failed to create cloned template');
      }
      
      toast({
        title: 'Success',
        description: 'QC template cloned successfully',
      });
      
      return result;
    } catch (error) {
      console.error('Error cloning QC template:', error);
      toast({
        title: 'Error',
        description: 'Failed to clone QC template',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, fetchTemplateById, generateTemplateCode, createTemplate]);

  // Update parameter sequence numbers for order changes
  const updateParameterOrder = useCallback(async (updatedParameters: QCParameter[]) => {
    try {
      setLoading(true);
      
      // Prepare updates with new sequence numbers
      for (const param of updatedParameters) {
        const { error } = await supabase
          .from('qc_parameters')
          .update({ sequence_no: param.sequence_no })
          .eq('id', param.id);
        
        if (error) {
          throw error;
        }
      }
      
      // If we have a template loaded, update its parameters
      if (template) {
        setTemplate({
          ...template,
          parameters: updatedParameters
        });
      }
      
      toast({
        title: 'Success',
        description: 'Parameter order updated successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error updating parameter order:', error);
      toast({
        title: 'Error',
        description: 'Failed to update parameter order',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, template]);

  // Delete a specific parameter
  const deleteParameter = useCallback(async (parameterId: string, templateId: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('qc_parameters')
        .delete()
        .eq('id', parameterId);
      
      if (error) {
        throw error;
      }
      
      // If we have a template loaded, update its parameters
      if (template && template.id === templateId) {
        setTemplate({
          ...template,
          parameters: template.parameters.filter(p => p.id !== parameterId),
          parameter_count: template.parameter_count - 1
        });
      }
      
      toast({
        title: 'Success',
        description: 'Parameter deleted successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting parameter:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete parameter',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [toast, template]);

  // Add a new parameter to an existing template
  const addParameter = useCallback(async (
    templateId: string,
    parameterData: QCParameterFormData
  ) => {
    try {
      setLoading(true);
      
      // Get current highest sequence number
      let sequenceNumber = 10;
      if (template && template.parameters.length > 0) {
        const maxSequence = Math.max(...template.parameters.map(p => p.sequence_no));
        sequenceNumber = maxSequence + 10;
      }
      
      // Insert new parameter
      const { data, error } = await supabase
        .from('qc_parameters')
        .insert({
          template_id: templateId,
          parameter_name: parameterData.parameter_name,
          uom_id: parameterData.uom_id || null, // The database trigger will handle UOM code to ID conversion
          min_spec: parameterData.min_spec !== null ? parameterData.min_spec : null,
          max_spec: parameterData.max_spec !== null ? parameterData.max_spec : null,
          tolerance: parameterData.tolerance || null,
          method: parameterData.method,
          is_required: parameterData.is_required || false,
          sequence_no: parameterData.sequence_no || sequenceNumber
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      // If we have a template loaded, update its parameters
      if (template && template.id === templateId) {
        setTemplate({
          ...template,
          parameters: [...template.parameters, data],
          parameter_count: template.parameter_count + 1
        });
      }
      
      toast({
        title: 'Success',
        description: 'Parameter added successfully',
      });
      
      return data;
    } catch (error) {
      console.error('Error adding parameter:', error);
      toast({
        title: 'Error',
        description: 'Failed to add parameter',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, template]);

  // Update an existing parameter
  const updateParameter = useCallback(async (
    parameterId: string,
    templateId: string,
    parameterData: QCParameterFormData
  ) => {
    try {
      setLoading(true);
      
      // Update the parameter
      const { data, error } = await supabase
        .from('qc_parameters')
        .update({
          parameter_name: parameterData.parameter_name,
          uom_id: parameterData.uom_id || null, // The database trigger will handle UOM code to ID conversion
          min_spec: parameterData.min_spec !== null ? parameterData.min_spec : null,
          max_spec: parameterData.max_spec !== null ? parameterData.max_spec : null,
          tolerance: parameterData.tolerance || null,
          method: parameterData.method,
          is_required: parameterData.is_required || false,
          sequence_no: parameterData.sequence_no || 10
        })
        .eq('id', parameterId)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      // If we have a template loaded, update its parameters
      if (template && template.id === templateId) {
        setTemplate({
          ...template,
          parameters: template.parameters.map(p => p.id === parameterId ? data : p)
        });
      }
      
      toast({
        title: 'Success',
        description: 'Parameter updated successfully',
      });
      
      return data;
    } catch (error) {
      console.error('Error updating parameter:', error);
      toast({
        title: 'Error',
        description: 'Failed to update parameter',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [toast, template]);

  // Lookup UOM by code
  const lookupUOMByCode = useCallback(async (uomCode: string) => {
    try {
      const { data, error } = await supabase.rpc('get_uom_id_by_code', {
        code_input: uomCode
      });
      
      if (error) {
        throw error;
      }
      
      return data; // This will be the UOM ID if found, or null if not
    } catch (error) {
      console.error('Error looking up UOM by code:', error);
      return null;
    }
  }, []);

  return {
    loading,
    templates,
    template,
    fetchTemplates,
    fetchTemplateById,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    changeTemplateStatus,
    checkTemplateCodeExists,
    generateTemplateCode,
    // Parameter management functions
    addParameter,
    updateParameter,
    deleteParameter,
    updateParameterOrder,
    // Template assignment functions
    assignTemplateToOperation,
    removeTemplateFromOperation,
    assignTemplateToComponent,
    removeTemplateFromComponent,
    fetchRoutingOperationsWithQC,
    fetchBOMComponentsWithQC,
    // UOM utilities
    lookupUOMByCode,
    // Advanced functions
    cloneTemplate
  };
};
